import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageHero from '../components/layout/PageHero';
import ProjectsDisplay from '../components/layout/ProjectsDisplay';
import { getRealEstatePrograms } from '../data/realEstatePrograms';

// Import de la fonction depuis le fichier data/realEstatePrograms.js

const PromotionImmobiliere = () => {
  const { t } = useTranslation();

  // Mise à jour du titre de la page
  useEffect(() => {
    document.title = `${t('header.realEstate')} | SOCABEG`;
  }, [t]);
  
  // Récupérer les programmes immobiliers
  const realEstatePrograms = getRealEstatePrograms(t);

  const [searchFilters, setSearchFilters] = useState({
    propertyType: '',
    maxBudget: '',
    location: ''
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Vérifier si au moins un champ est rempli
    if (!searchFilters.propertyType && !searchFilters.maxBudget && !searchFilters.location) {
      setSearchError(t('realEstate.searchError'));
      return;
    }
    
    // Réinitialiser l'erreur si tout est ok
    setSearchError('');
    console.log('Recherche avec filtres:', searchFilters);
    setHasSearched(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReset = () => {
    setSearchFilters({
      propertyType: '',
      maxBudget: '',
      location: ''
    });
    setHasSearched(false);
    setSearchError('');
  };

  // Parse un prix numérique à partir d'une chaîne (ex: "À PARTIR DE 17 000 000 FCFA")
  const parsePrice = (price) => {
    if (!price) return null;
    const match = price.match(/\d[\d\s]*/);
    if (!match) return null;
    return parseInt(match[0].replace(/\s/g, ''), 10) || null;
  };

  const maxBudgetValue = searchFilters.maxBudget
    ? parseInt(searchFilters.maxBudget.replace(/\s/g, ''), 10)
    : null;

  const filteredPrograms = realEstatePrograms.filter((program) => {
    const matchType = !searchFilters.propertyType
      || program.propertyTypeKey === searchFilters.propertyType;

    const matchLocation = !searchFilters.location
      || program.locationKey === searchFilters.location;

    const programPrice = parsePrice(program.price);
    const matchBudget = !maxBudgetValue
      || !programPrice
      || programPrice <= maxBudgetValue;

    return matchType && matchLocation && matchBudget;
  });

  const programsToDisplay = hasSearched ? filteredPrograms : realEstatePrograms;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <PageHero 
        backgroundImage="/images/hero_immobilier.jpg"
        title={t('realEstate.hero.title')}
        subtitle={t('realEstate.hero.subtitle')}
        overlayColor="socabeg-blue"
        textPosition="left"
      />

      {/* Search Section */}
      <div className="container mx-auto px-4 pt-8">
        <div className="max-w-4xl mx-auto bg-white/90 backdrop-blur-sm rounded-xl shadow-2xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">🔎 {t('realEstate.searchTitle')}</h2>
          <p className="text-gray-700 text-center mb-6">
            {t('realEstate.searchDescription')}
          </p>
          
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('realEstate.propertyType')}
                </label>
                <select
                  name="propertyType"
                  value={searchFilters.propertyType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-socabeg-blue focus:border-socabeg-blue"
                >
                  <option value="">{t('realEstate.selectPropertyType')}</option>
                  <option value="villa">{t('realEstate.propertyTypes.house')}</option>
                  <option value="appartement">{t('realEstate.propertyTypes.apartment')}</option>
                  <option value="Boutiques">{t('realEstate.propertyTypes.commercial')}</option>
                  <option value="parcelles">{t('realEstate.propertyTypes.land')}</option>
                  <option value="logements_sociaux">{t('realEstate.propertyTypes.socialHousing')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('realEstate.maxBudget')}
                </label>
                <input
                  type="text"
                  name="maxBudget"
                  value={searchFilters.maxBudget}
                  onChange={handleInputChange}
                  placeholder={t('realEstate.budgetPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-socabeg-blue focus:border-socabeg-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('realEstate.location')}
                </label>
                <select
                  name="location"
                  value={searchFilters.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-socabeg-blue focus:border-socabeg-blue"
                >
                  <option value="">{t('realEstate.selectLocation')}</option>
                  <option value="almadies">{t('realEstate.locations.almadies')}</option>
                  <option value="almadies2">{t('realEstate.locations.almadies2')}</option>
                  <option value="tivaouanePeulh">{t('realEstate.locations.tivaouanePeulh')}</option>
                  <option value="ndiakhirate">{t('realEstate.locations.ndiakhirate')}</option>
                  <option value="hlm5">{t('realEstate.locations.hlm5')}</option>
                </select>
              </div>
            </div>
            
            {searchError && (
              <div className="text-red-600 text-center font-medium mt-2">
                {searchError}
              </div>
            )}
            
            <div className="flex items-center justify-center gap-4 pt-2">
              <button
                type="submit"
                className="bg-socabeg-blue hover:bg-socabeg-blue/90 text-white font-medium py-3 px-8 rounded-full transition-colors duration-300 inline-flex items-center"
              >
                {t('realEstate.searchButton')}
              </button>

              {hasSearched && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="border border-socabeg-blue text-socabeg-blue hover:bg-socabeg-blue/5 font-medium py-3 px-6 rounded-full transition-colors duration-300 text-sm"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

          {/* Liste des programmes */}
          <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-center">{t('realEstate.programsTitle')}</h2>
          </div>
          
          <ProjectsDisplay 
            projects={programsToDisplay} 
            showMoreButton={false}
            initialCount={100} /* Valeur élevée pour afficher tous les programmes */
          />
        </div>
      </section>
    </div>
  );
};

export default PromotionImmobiliere;
